/**
 * 外交系统状态管理
 */

import { create } from 'zustand'
import type {
  DiplomacyState,
  FactionRelation,
  Treaty,
  MarriageAlliance,
  Hostage,
  DiplomaticEvent,
  DiplomaticEnvoy
} from '../types/diplomacy'
import { SHANG_FACTIONS } from '../data/shangDynastyData'

// 初始状态
const initialState: DiplomacyState = {
  relations: new Map(),
  treaties: new Map(),
  marriages: new Map(),
  hostages: new Map(),
  events: new Map(),
  envoys: new Map(),
  tributarySystem: {
    suzerains: new Map(),
    vassals: new Map()
  },
  statistics: {
    totalDiplomaticActions: 0,
    totalTreaties: 0,
    totalMarriages: 0,
    totalWars: 0,
    totalPeaceTreaties: 0
  }
}

// 创建初始关系
function initializeRelations() {
  const relations = new Map<string, Map<string, FactionRelation>>()
  
  SHANG_FACTIONS.forEach(faction => {
    const factionRelations = new Map<string, FactionRelation>()
    
    SHANG_FACTIONS.forEach(targetFaction => {
      if (faction.id !== targetFaction.id) {
        factionRelations.set(targetFaction.id, {
          factionId: targetFaction.id,
          attitude: 0, // 初始中立
          trust: 50,
          fear: 20,
          respect: 30,
          interests: {
            territorial: 0,
            economic: 0,
            military: 0,
            cultural: 0
          },
          status: 'neutral',
          history: [],
          interactions: {
            wars: 0,
            alliances: 0,
            trades: 0,
            lastContact: { year: 1046, month: 1, day: 1 }
          }
        })
      }
    })
    
    relations.set(faction.id, factionRelations)
  })
  
  return relations
}

// 初始化状态
initialState.relations = initializeRelations()

interface DiplomacyStore extends DiplomacyState {
  // 关系管理
  getRelation: (factionId: string, targetFactionId: string) => FactionRelation | null
  updateRelation: (factionId: string, targetFactionId: string, updates: Partial<FactionRelation>) => void
  improveRelations: (factionId: string, targetFactionId: string, amount: number) => void
  
  // 条约管理
  createTreaty: (treaty: Omit<Treaty, 'id' | 'signedAt' | 'expiresAt'>) => string
  updateTreaty: (treatyId: string, updates: Partial<Treaty>) => void
  breakTreaty: (treatyId: string, reason: string) => void
  
  // 联姻管理
  createMarriage: (marriage: Omit<MarriageAlliance, 'id' | 'arrangedAt'>) => string
  completeMarriage: (marriageId: string) => void
  annulMarriage: (marriageId: string, reason: string) => void
  
  // 质子管理
  sendHostage: (hostage: Omit<Hostage, 'id' | 'sentAt'>) => string
  returnHostage: (hostageId: string) => void
  
  // 外交事件
  createEvent: (event: Omit<DiplomaticEvent, 'id' | 'occurredAt'>) => string
  resolveEvent: (eventId: string, optionId: string) => void
  
  // 使节管理
  sendEnvoy: (envoy: Omit<DiplomaticEnvoy, 'id' | 'startedAt' | 'expectedReturnAt'>) => string
  recallEnvoy: (envoyId: string) => void
  
  // 朝贡体系
  establishVassalage: (vassalFactionId: string, suzerainFactionId: string) => void
  endVassalage: (vassalFactionId: string) => void
  
  // 统计
  incrementStatistic: (stat: keyof DiplomacyState['statistics']) => void
}

export const useDiplomacyStore = create<DiplomacyStore>((set, get) => ({
  ...initialState,
  
  // 关系管理
  getRelation: (factionId, targetFactionId) => {
    const relations = get().relations.get(factionId)
    return relations?.get(targetFactionId) || null
  },
  
  updateRelation: (factionId, targetFactionId, updates) => {
    set(state => {
      const newRelations = new Map(state.relations)
      const factionRelations = new Map(newRelations.get(factionId) || new Map())
      const currentRelation = factionRelations.get(targetFactionId)
      
      if (currentRelation) {
        factionRelations.set(targetFactionId, {
          ...currentRelation,
          ...updates
        })
        newRelations.set(factionId, factionRelations)
      }
      
      return { relations: newRelations }
    })
  },
  
  improveRelations: (factionId, targetFactionId, amount) => {
    const relation = get().getRelation(factionId, targetFactionId)
    if (relation) {
      get().updateRelation(factionId, targetFactionId, {
        attitude: Math.max(-100, Math.min(100, relation.attitude + amount)),
        trust: Math.max(0, Math.min(100, relation.trust + amount * 0.5))
      })
    }
  },
  
  // 条约管理
  createTreaty: (treaty) => {
    const id = `treaty_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 } // 临时日期
    
    set(state => {
      const newTreaties = new Map(state.treaties)
      newTreaties.set(id, {
        ...treaty,
        id,
        signedAt: now,
        expiresAt: { ...now, year: now.year + Math.floor(treaty.terms.duration / 12) }
      })
      
      return {
        treaties: newTreaties,
        statistics: {
          ...state.statistics,
          totalTreaties: state.statistics.totalTreaties + 1
        }
      }
    })
    
    return id
  },
  
  updateTreaty: (treatyId, updates) => {
    set(state => {
      const newTreaties = new Map(state.treaties)
      const treaty = newTreaties.get(treatyId)
      
      if (treaty) {
        newTreaties.set(treatyId, { ...treaty, ...updates })
      }
      
      return { treaties: newTreaties }
    })
  },
  
  breakTreaty: (treatyId, _reason) => {
    get().updateTreaty(treatyId, { status: 'broken' })
  },
  
  // 联姻管理
  createMarriage: (marriage) => {
    const id = `marriage_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newMarriages = new Map(state.marriages)
      newMarriages.set(id, {
        ...marriage,
        id,
        arrangedAt: now,
        status: 'arranged'
      })
      
      return {
        marriages: newMarriages,
        statistics: {
          ...state.statistics,
          totalMarriages: state.statistics.totalMarriages + 1
        }
      }
    })
    
    return id
  },
  
  completeMarriage: (marriageId) => {
    const now = { year: 1046, month: 1, day: 1 }
    set(state => {
      const newMarriages = new Map(state.marriages)
      const marriage = newMarriages.get(marriageId)
      if (marriage) {
        newMarriages.set(marriageId, { ...marriage, status: 'completed', marriedAt: now })
      }
      return { marriages: newMarriages }
    })
  },

  annulMarriage: (marriageId, _reason) => {
    const now = { year: 1046, month: 1, day: 1 }
    set(state => {
      const newMarriages = new Map(state.marriages)
      const marriage = newMarriages.get(marriageId)
      if (marriage) {
        newMarriages.set(marriageId, { ...marriage, status: 'annulled', annulledAt: now })
      }
      return { marriages: newMarriages }
    })
  },
  
  // 质子管理
  sendHostage: (hostage) => {
    const id = `hostage_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newHostages = new Map(state.hostages)
      newHostages.set(id, {
        ...hostage,
        id,
        sentAt: now,
        status: 'held'
      })
      
      return { hostages: newHostages }
    })
    
    return id
  },
  
  returnHostage: (hostageId) => {
    const now = { year: 1046, month: 1, day: 1 }
    set(state => {
      const newHostages = new Map(state.hostages)
      const hostage = newHostages.get(hostageId)
      
      if (hostage) {
        newHostages.set(hostageId, { ...hostage, status: 'returned', returnedAt: now })
      }
      
      return { hostages: newHostages }
    })
  },
  
  // 外交事件
  createEvent: (event) => {
    const id = `event_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newEvents = new Map(state.events)
      newEvents.set(id, {
        ...event,
        id,
        occurredAt: now,
        status: 'active'
      })
      
      return { events: newEvents }
    })
    
    return id
  },
  
  resolveEvent: (eventId, _optionId) => {
    const now = { year: 1046, month: 1, day: 1 }
    set(state => {
      const newEvents = new Map(state.events)
      const event = newEvents.get(eventId)

      if (event) {
        newEvents.set(eventId, { ...event, status: 'resolved', resolvedAt: now })
      }

      return { events: newEvents }
    })
  },
  
  // 使节管理
  sendEnvoy: (envoy) => {
    const id = `envoy_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    const expectedReturnAt = { ...now, day: now.day + envoy.mission.duration }
    
    set(state => {
      const newEnvoys = new Map(state.envoys)
      newEnvoys.set(id, {
        ...envoy,
        id,
        startedAt: now,
        expectedReturnAt,
        status: 'preparing'
      })
      
      return {
        envoys: newEnvoys,
        statistics: {
          ...state.statistics,
          totalDiplomaticActions: state.statistics.totalDiplomaticActions + 1
        }
      }
    })
    
    return id
  },
  
  recallEnvoy: (envoyId) => {
    set(state => {
      const newEnvoys = new Map(state.envoys)
      const envoy = newEnvoys.get(envoyId)
      
      if (envoy) {
        newEnvoys.set(envoyId, { ...envoy, status: 'returning' })
      }
      
      return { envoys: newEnvoys }
    })
  },
  
  // 朝贡体系
  establishVassalage: (vassalFactionId, suzerainFactionId) => {
    set(state => {
      const newTributarySystem = { ...state.tributarySystem }
      
      // 更新宗主国
      const vassals = new Map(newTributarySystem.suzerains)
      const existingVassals = vassals.get(suzerainFactionId) || []
      if (!existingVassals.includes(vassalFactionId)) {
        vassals.set(suzerainFactionId, [...existingVassals, vassalFactionId])
      }
      
      // 更新附庸国
      const suzerains = new Map(newTributarySystem.vassals)
      suzerains.set(vassalFactionId, suzerainFactionId)
      
      return {
        tributarySystem: {
          suzerains: vassals,
          vassals: suzerains
        }
      }
    })
  },
  
  endVassalage: (vassalFactionId) => {
    set(state => {
      const newTributarySystem = { ...state.tributarySystem }
      const suzerainFactionId = newTributarySystem.vassals.get(vassalFactionId)
      
      if (suzerainFactionId) {
        // 更新宗主国
        const vassals = new Map(newTributarySystem.suzerains)
        const existingVassals = vassals.get(suzerainFactionId) || []
        vassals.set(suzerainFactionId, existingVassals.filter(id => id !== vassalFactionId))
        
        // 更新附庸国
        const suzerains = new Map(newTributarySystem.vassals)
        suzerains.delete(vassalFactionId)
        
        return {
          tributarySystem: {
            suzerains: vassals,
            vassals: suzerains
          }
        }
      }
      
      return state
    })
  },
  
  // 统计
  incrementStatistic: (stat) => {
    set(state => ({
      statistics: {
        ...state.statistics,
        [stat]: state.statistics[stat] + 1
      }
    }))
  },
  
}))
